'use client';
/**
 * Everything the export flow knows about `ExportOptions`:
 *
 *  - `useOptionsSummary` turns a set of options into one plain sentence ("a print-quality PDF, one
 *    page per sheet, cover as a separate file") — the dialog's whole simple view is that sentence,
 *    so nothing is ever hidden by surprise;
 *  - `estimateExportBytes` is the size guess shown next to it;
 *  - `loadOptions` / `storeOptions` remember the last choice per album on this device;
 *  - `<AdvancedOptions>` is the collapsed disclosure — the ONLY place the seven knobs appear.
 */
import * as React from 'react';
import { useTranslations } from 'next-intl';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import {
  ExportOptions,
  PRINT_SERVICE_IDS,
  nearestServiceSize,
  printService,
  resolveExport,
  type AlbumDocument,
  type CoverMode,
  type PrintServiceProfile,
} from '@freealbum/document';
import { SegmentedControl, Select, Slider, Switch, cn } from '@freealbum/ui';
import { clamp } from '@/lib/utils';

// ───────────────────────── per-album memory ─────────────────────────

/**
 * One entry per album: someone who prints one album at 600 DPI and another at 300 should not have
 * to re-open the disclosure every time. The pre-per-album key is still read once, so a returning
 * user keeps whatever they last chose.
 */
const OPTIONS_KEY = (albumId: string) => `freealbum.export.options:${albumId}`;
const LEGACY_OPTIONS_KEY = 'freealbum.export.options';

export const defaultExportOptions = (): ExportOptions => ExportOptions.parse({});

function readOptions(key: string): ExportOptions | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = ExportOptions.safeParse(JSON.parse(raw) as unknown);
    return parsed.success ? parsed.data : null;
  } catch {
    // Private mode, a quota wall, or a shape we no longer understand — fall back to the defaults.
    return null;
  }
}

export interface StoredOptions {
  options: ExportOptions;
  /**
   * False when nothing was found for this album on this device, which lets the dialog adopt the
   * options of the album's last finished export instead — memory that survives a new phone.
   */
  remembered: boolean;
}

export function loadOptions(albumId: string): StoredOptions {
  if (typeof window === 'undefined') return { options: defaultExportOptions(), remembered: false };
  const mine = readOptions(OPTIONS_KEY(albumId));
  if (mine) return { options: mine, remembered: true };
  const legacy = readOptions(LEGACY_OPTIONS_KEY);
  if (legacy) return { options: legacy, remembered: true };
  return { options: defaultExportOptions(), remembered: false };
}

export function storeOptions(albumId: string, options: ExportOptions): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(OPTIONS_KEY(albumId), JSON.stringify(options));
  } catch {
    /* the export still works; we just won't remember the choice */
  }
}

/** True when `options` is exactly what a fresh album would export with. */
export function isDefaultOptions(options: ExportOptions): boolean {
  const d = defaultExportOptions();
  return (
    options.dpi === d.dpi &&
    options.jpegQuality === d.jpegQuality &&
    options.pageMode === d.pageMode &&
    options.cover === d.cover &&
    options.printReady === d.printReady &&
    options.flipbook === d.flipbook &&
    options.printService === d.printService
  );
}

// ───────────────────────── the sentence ─────────────────────────

export interface OptionsSummary {
  /** One sentence, no jargon unless the reader asked for it by raising the DPI. */
  sentence: string;
  /** Short pills for the extras that are on — print preparation, page-flip link. */
  tags: string[];
}

/**
 * Returns a formatter rather than a string so the dialog can describe both the options it is about
 * to use AND the options a past job actually used, in the same words.
 */
export function useOptionsSummary(): (options: ExportOptions) => OptionsSummary {
  const t = useTranslations('export');
  return React.useCallback(
    (options: ExportOptions): OptionsSummary => {
      const quality = options.dpi > defaultExportOptions().dpi ? t('summaryQualityHigh', { dpi: options.dpi }) : t('summaryQualityPrint');
      const service = printService(options.printService);
      if (service) {
        // The shop's rules are the sentence; only the flip link is still the person's own extra.
        const tags = options.flipbook ? [t('summaryFlipbook')] : [];
        return { sentence: t('summaryService', { quality, service: t(`services.${service.id}.name`), rules: t(`services.${service.id}.shape`) }), tags };
      }
      const pages = options.pageMode === 'single' ? t('summaryPagesSingle') : t('summaryPagesSpread');
      const cover =
        options.cover === 'separate' ? t('summaryCoverSeparate') : options.cover === 'bound' ? t('summaryCoverBound') : t('summaryCoverNone');
      const tags: string[] = [];
      if (options.printReady) tags.push(t('summaryPrintReady'));
      if (options.flipbook) tags.push(t('summaryFlipbook'));
      return { sentence: t('summary', { quality, pages, cover }), tags };
    },
    [t],
  );
}

// ───────────────────────── size estimate ─────────────────────────

const MM_PER_INCH = 25.4;
/** Fallback page size when the caller has no document to hand (200×200 mm = sq20). */
const ASSUMED_PAGE_MM = 200;
const BLEED_MM = 3;
/**
 * Bytes per pixel for a detailed photographic 4:2:0 JPEG at quality 90 (≈0.9 bit/px). Measured
 * mid-range for print-resolution album pages; snapshot-y pages compress better, dense collages worse.
 */
const BYTES_PER_PIXEL_Q90 = 0.11;
/** libjpeg's size curve steepens near 100, so quality enters quadratically: q60 ≈ 0.44×, q100 ≈ 1.23×. */
const QUALITY_REFERENCE = 90;
/** PDF object/xref/font overhead on top of the image streams. */
const PDF_OVERHEAD_FACTOR = 1.02;
const PDF_FIXED_BYTES = 50 * 1024;
const ESTIMATE_MIN_BYTES = 1024 * 1024;
const ESTIMATE_MAX_BYTES = 8 * 1024 ** 3;

/**
 * Rough export size estimate, assuming every page is fully covered by imagery (the worst case,
 * and the common case for a photo album):
 *
 *   pixelsPerPage = (widthMm / 25.4 · dpi) · (heightMm / 25.4 · dpi)
 *   bytesPerPage  = pixelsPerPage · 0.11 · (jpegQuality / 90)²
 *   total         = bytesPerPage · (innerPages + coverPages) · 1.02 + 50 KB
 *
 * Adjustments and deliberate omissions:
 *  - `printReady` adds 3 mm of bleed on every side, i.e. +6 mm on each axis, before the DPI maths.
 *  - `cover: 'separate' | 'bound'` adds ~2 page-equivalents (front + back of the wrap). 'separate'
 *    only splits the same bytes across two files, so the total is identical either way.
 *  - `pageMode` does not appear: a spread PDF page is two pages wide, so the imaged area — and
 *    therefore the byte count — is the same for 'single' and 'spread'.
 *  - `flipbook` renders screen-resolution page images for the viewer link; those live next to the
 *    PDF rather than inside it, so they are excluded from the download estimate.
 *
 * Clamped to 1 MB … 8 GB so a nonsense page count can never render an absurd number.
 */
export function estimateExportBytes(
  pageCount: number,
  options: ExportOptions,
  page: { widthMm: number; heightMm: number } = { widthMm: ASSUMED_PAGE_MM, heightMm: ASSUMED_PAGE_MM },
): number {
  const service = printService(options.printService);
  const bleed = service ? service.page.bleedMm * 2 : options.printReady ? BLEED_MM * 2 : 0;
  const widthPx = ((page.widthMm + bleed) / MM_PER_INCH) * options.dpi;
  const heightPx = ((page.heightMm + bleed) / MM_PER_INCH) * options.dpi;
  const qualityFactor = (options.jpegQuality / QUALITY_REFERENCE) ** 2;
  const bytesPerPage = widthPx * heightPx * BYTES_PER_PIXEL_Q90 * qualityFactor;
  const coverPages = options.cover === 'none' ? 0 : 2;
  const pages = Math.max(0, Math.round(Number.isFinite(pageCount) ? pageCount : 0)) + coverPages;
  const total = bytesPerPage * pages * PDF_OVERHEAD_FACTOR + PDF_FIXED_BYTES;
  return clamp(total, ESTIMATE_MIN_BYTES, ESTIMATE_MAX_BYTES);
}

// ───────────────────────── the disclosure ─────────────────────────

export interface AdvancedOptionsProps {
  options: ExportOptions;
  onChange: (patch: Partial<ExportOptions>) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** what a print service is measured against: the page, the page count and the binding */
  page: { widthMm: number; heightMm: number };
  pageCount: number;
  rtl: boolean;
  /** the live document when the dialog sits in the editor; lets the note state the cover file exactly */
  doc: AlbumDocument | null;
  /** Rendered inside the panel, under the controls — the quiet links the dialog wants out of the way. */
  children?: React.ReactNode;
}

/**
 * What choosing a shop means for this album: its rules in one line, where to upload, and the two
 * things worth knowing before paying — whether the page size is one they sell, and whether the
 * page count is one they bind.
 */
export function PrintServiceNote({ profile, page, pageCount, rtl, doc, options }: {
  profile: PrintServiceProfile;
  page: { widthMm: number; heightMm: number };
  pageCount: number;
  rtl: boolean;
  doc: AlbumDocument | null;
  options: ExportOptions;
}): React.JSX.Element {
  const t = useTranslations('export');
  const match = nearestServiceSize(profile, page);
  const size = t('inches', { w: match.size.widthIn, h: match.size.heightIn });
  const resolved = doc ? resolveExport(doc, options) : null;
  const spec = resolved?.coverSpec ?? null;
  const round1 = (v: number) => Math.round(v * 10) / 10;
  return (
    <div className="flex flex-col gap-1.5 rounded-md bg-paper-2 p-3 text-xs text-ink-2" data-testid="print-service-note">
      <p>{t(`services.${profile.id}.rules`)}</p>
      {resolved && spec ? (
        <p>
          {t('bindingLine', { binding: spec.label, pages: pageCount })}
          {resolved.cover === 'separate'
            ? ` ${t('coverModeHint.separate', { wrap: round1(Math.max(resolved.coverBleed.topMm, resolved.coverBleed.leftMm)), spine: round1(resolved.coverSpineMm) })}`
            : resolved.cover === 'bound' ? ` ${t('coverModeHint.bound')}` : ` ${t('coverModeHint.none')}`}
          {resolved.cover === 'separate' && spec.spineEstimated ? ` ${t('spineEstimated')}` : ''}
        </p>
      ) : null}
      <p>
        {t(`services.${profile.id}.upload`)}{' '}
        <a href={profile.url} target="_blank" rel="noreferrer" className="font-medium text-accent underline underline-offset-2">
          {t(`services.${profile.id}.link`)}
        </a>
      </p>
      {rtl ? <p>{t('serviceRtl', { service: t(`services.${profile.id}.name`) })}</p> : null}
      <p>{match.exact ? t('serviceSizeExact', { size }) : t('serviceSizeNear', { size })}</p>
      {spec && pageCount < spec.pages.min ? (
        <p className="text-warning">{t('servicePagesLow', { min: spec.pages.min, count: pageCount })}</p>
      ) : spec && pageCount > spec.pages.max ? (
        <p className="text-warning">{t('servicePagesHigh', { max: spec.pages.max, count: pageCount })}</p>
      ) : null}
    </div>
  );
}

/**
 * A real `button[aria-expanded]` over a panel that stays in the DOM (hidden), so the toggle can own
 * it with `aria-controls` and a screen reader is told what it controls before it opens.
 */
export function AdvancedOptions({ options, onChange, open, onOpenChange, page, pageCount, rtl, doc, children }: AdvancedOptionsProps): React.JSX.Element {
  const t = useTranslations('export');
  const panelId = React.useId();
  const profile = printService(options.printService);
  // A shop takes the cover in some ways and not others; its list replaces the general one.
  const shopModes: readonly CoverMode[] = profile
    ? (profile.covers[doc?.print.binding ?? 'hardcover']?.modes ?? profile.covers.hardcover?.modes ?? [])
    : ['separate', 'bound'];
  const coverModes = Array.from(new Set<CoverMode>([...shopModes, 'none']));
  const coverLabel = (mode: CoverMode) =>
    profile ? t(`coverMode.${mode}`) : mode === 'separate' ? t('coverSeparate') : mode === 'bound' ? t('coverBound') : t('coverNone');

  return (
    <section className="border-t border-line pt-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        // The hint under the label disappears once the panel is open; naming the toggle explicitly
        // keeps its accessible name from changing under a screen reader mid-interaction.
        aria-label={t('advanced')}
        onClick={() => onOpenChange(!open)}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-1 py-2 text-start',
          'transition-colors duration-(--duration-fast) hover:bg-paper-2',
          'focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-1',
        )}
      >
        <SlidersHorizontal aria-hidden className="size-4 shrink-0 text-ink-3" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-ink">{t('advanced')}</span>
          {!open ? <span className="block text-xs text-ink-3">{t('advancedHint')}</span> : null}
        </span>
        <ChevronDown aria-hidden className={cn('size-4 shrink-0 text-ink-3 transition-transform duration-(--duration-fast)', open && 'rotate-180')} />
      </button>

      <div id={panelId} hidden={!open} className="flex flex-col gap-5 px-1 pt-3 pb-1">
        <div className="flex flex-col gap-2">
          <Select
            label={t('printService')}
            hint={profile ? undefined : t('printServiceHint')}
            value={options.printService}
            onChange={(e) => {
              // Switching shop also switches to the way that shop prefers its cover; the person can still change it below.
              const next = e.target.value as ExportOptions['printService'];
              const spec = printService(next)?.covers[doc?.print.binding ?? 'hardcover'];
              onChange({ printService: next, ...(spec && options.cover !== 'none' ? { cover: spec.modes[0] } : {}) });
            }}
            options={[
              { value: 'none', label: t('printServiceNone') },
              ...PRINT_SERVICE_IDS.map((id) => ({ value: id, label: t(`services.${id}.name`) })),
            ]}
          />
          {profile ? <PrintServiceNote profile={profile} page={page} pageCount={pageCount} rtl={rtl} doc={doc} options={options} /> : null}
        </div>

        <Slider
          label={t('dpi')}
          formatValue={(v) => t('dpiValue', { dpi: v })}
          value={options.dpi}
          onValueChange={(v) => onChange({ dpi: Math.round(v) })}
          min={300}
          max={1200}
          step={50}
          marks={[300, 600, 900, 1200]}
        />
        <p className="-mt-4 text-xs text-ink-3">{t('dpiHint')}</p>

        <Slider
          label={t('quality')}
          value={options.jpegQuality}
          onValueChange={(v) => onChange({ jpegQuality: Math.round(v) })}
          min={60}
          max={100}
          step={1}
        />

        {/* A shop decides these three; the controls stay visible so the choice is not a mystery, but they are off. */}
        <fieldset disabled={profile !== null} className="m-0 flex min-w-0 flex-col gap-5 border-0 p-0 disabled:opacity-60" aria-describedby={profile ? `${panelId}-locked` : undefined}>
        {profile ? <p id={`${panelId}-locked`} className="text-xs text-ink-3">{t('serviceLocked', { service: t(`services.${profile.id}.name`) })}</p> : null}
        <OptionGroup label={t('pageMode')} hint={t('pageModeHint')}>
          <SegmentedControl
            aria-label={t('pageMode')}
            value={options.pageMode}
            onValueChange={(v) => onChange({ pageMode: v })}
            options={[
              { value: 'single', label: t('pageModeSingle') },
              { value: 'spread', label: t('pageModeSpread') },
            ]}
            fullWidth
          />
        </OptionGroup>

        </fieldset>

        <OptionGroup label={t('cover')}>
          <SegmentedControl
            aria-label={t('cover')}
            value={coverModes.includes(options.cover) ? options.cover : coverModes[0]!}
            onValueChange={(v) => onChange({ cover: v })}
            options={coverModes.map((mode) => ({ value: mode, label: coverLabel(mode) }))}
            fullWidth
          />
        </OptionGroup>

        <fieldset disabled={profile !== null} className="m-0 flex min-w-0 flex-col gap-5 border-0 p-0 disabled:opacity-60">
        <Switch
          checked={options.printReady}
          onCheckedChange={(v) => onChange({ printReady: v })}
          label={t('printReady')}
          description={t('printReadyHint')}
        />
        </fieldset>

        <Switch
          checked={options.flipbook}
          onCheckedChange={(v) => onChange({ flipbook: v })}
          label={t('flipbook')}
          description={t('flipbookHint')}
        />

        {!isDefaultOptions(options) ? (
          <button
            type="button"
            onClick={() => onChange(defaultExportOptions())}
            className="self-start rounded-md text-xs font-medium text-accent underline underline-offset-2 hover:text-accent-hover focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-2"
          >
            {t('advancedReset')}
          </button>
        ) : null}

        {children}
      </div>
    </section>
  );
}

/** Label + hint around a control that is not a form field (segmented controls, sliders). */
function OptionGroup({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink">{label}</span>
      {children}
      {hint ? <span className="text-xs text-ink-3">{hint}</span> : null}
    </div>
  );
}
